import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Mail, Copy, RefreshCw, Loader2, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const RESULT_LABELS = {
  "dropped:no_content": "verworfen: kein verwertbarer Inhalt",
  "dropped:auto_mail": "verworfen: automatische Mail",
  "dropped:daily_limit": "verworfen: Tageslimit erreicht",
  "dropped:disabled": "verworfen: Adresse pausiert",
  "dropped:not_pro": "verworfen: Pro erforderlich",
  "error": "Fehler bei der Verarbeitung",
};

const resultLabel = (r) => {
  if (r?.startsWith("created:")) return `${r.split(":")[1]} Beleg(e) angelegt`;
  return RESULT_LABELS[r] || r;
};

export const InboundEmailSection = () => {
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [needsPro, setNeedsPro] = useState(false);
  const [history, setHistory] = useState([]);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/inbound/address");
      setAddress(res.data.address);
      setEnabled(res.data.enabled);
      try {
        const h = await api.get("/inbound/history");
        setHistory(h.data || []);
      } catch { /* nicht kritisch */ }
    } catch (e) {
      if (e.response?.status === 403) setNeedsPro(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success("Adresse kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const rotate = async () => {
    if (!window.confirm("Neue Adresse erzeugen? Die bisherige Adresse wird sofort ungültig — Weiterleitungen und Lieferanten müssen aktualisiert werden.")) return;
    setRotating(true);
    try {
      const res = await api.post("/inbound/address/rotate");
      setAddress(res.data.address);
      setEnabled(true);
      trackEvent("inbox_address_rotated");
      toast.success("Neue Beleg-Adresse erzeugt");
    } catch {
      toast.error("Konnte keine neue Adresse erzeugen");
    } finally {
      setRotating(false);
    }
  };

  const toggle = async (val) => {
    setEnabled(val);
    try {
      await api.put("/inbound/address/toggle", { enabled: val });
      toast.success(val ? "Beleg-Eingang aktiviert" : "Beleg-Eingang pausiert");
    } catch {
      setEnabled(!val);
      toast.error("Änderung fehlgeschlagen");
    }
  };

  return (
    <Card data-testid="inbound-email-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-emerald-600" /> Beleg-Eingang per E-Mail
        </CardTitle>
        <CardDescription>
          Sende oder leite Rechnungen an deine persönliche Adresse weiter — sie erscheinen automatisch als
          Ausgaben-Entwürfe (KI-Extraktion, Duplikat-Prüfung) und warten in den Finanzen auf deine Bestätigung.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
        ) : needsPro ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="inbound-pro-required">
            <Lock className="h-4 w-4 shrink-0" />
            <span>Die Beleg-Mail-Adresse ist ein <strong>Pro-Feature</strong>. <Link to="/pricing" className="underline font-medium">Jetzt upgraden</Link></span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded-md bg-slate-100 border border-slate-200 px-3 py-2 text-sm font-mono select-all" data-testid="inbound-address">{address}</code>
              <Button size="sm" variant="outline" onClick={copyAddress} data-testid="inbox-address-copy">
                <Copy className="h-3.5 w-3.5 mr-1" /> Kopieren
              </Button>
              <Button size="sm" variant="ghost" onClick={rotate} disabled={rotating} data-testid="inbox-address-rotate" title="Neue Adresse erzeugen (alte wird ungültig)">
                {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </Button>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-slate-500">{enabled ? "Aktiv" : "Pausiert"}</span>
                <Switch checked={enabled} onCheckedChange={toggle} data-testid="inbox-address-toggle" />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              PDF- und Bild-Anhänge werden je Anhang als Beleg angelegt. Mails ohne Anhang werden aus dem
              Mail-Text erzeugt und deutlich als „kein Originalbeleg" markiert. Limit: 30 Mails/Tag.
            </p>
            {history.length > 0 && (
              <div className="space-y-1" data-testid="inbound-history">
                <p className="text-xs font-medium text-slate-600">Letzte Eingänge</p>
                {history.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between text-xs text-slate-500 border-b border-slate-100 py-1 gap-2">
                    <span className="truncate flex-1">{ev.from || "(unbekannt)"} · {ev.subject || "(ohne Betreff)"}</span>
                    <span className={`shrink-0 ${ev.result?.startsWith("created") ? "text-emerald-600" : "text-slate-400"}`}>{resultLabel(ev.result)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InboundEmailSection;
